import type { FooterTemplate } from '@iheartjane/data-access';
import { Card, Flex, Typography } from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import { useMaskito } from '@maskito/react';
import { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { urlMask } from '@jane/business-admin/util';

import { MaxCharacterHelper } from '../MaxCharacterHelper';
import { CTAWireframe } from './FooterWireframes';

import styles from './Footer.module.css';

const MAX_HEADER_LENGTH = 54;
const MAX_BODY_LENGTH = 105;
const MAX_BUTTON_TEXT_LENGTH = 28;

export const CTASection = () => {
  const urlRef = useMaskito({ options: urlMask });
  const { control, clearErrors } = useFormContext<FooterTemplate>();
  const formValues = useWatch({ control });

  const layout = formValues?.layout;
  const headerValue = formValues?.calls_to_action?.[0]?.header || '';
  const bodyValue = formValues?.calls_to_action?.[0]?.body || '';
  const buttonLabel = formValues?.calls_to_action?.[0]?.button_label || '';
  const destination = formValues?.calls_to_action?.[0]?.destination || '';

  // If destination is filled, button_label must be filled too
  const validateButtonLabel = useCallback(
    (value: unknown): string | true => {
      const stringValue = String(value || '');
      if (destination.trim() !== '' && stringValue.trim() === '') {
        return 'Add text that will display on the button!';
      }
      return true;
    },
    [destination]
  );

  // If button_label is filled, destination must be filled too
  const validateDestination = useCallback(
    (value: unknown): string | true => {
      const stringValue = String(value || '');
      if (buttonLabel.trim() !== '' && stringValue.trim() === '') {
        return 'Add the URL where the button will link to!';
      }
      return true;
    },
    [buttonLabel]
  );

  useEffect(() => {
    const bothFilled = buttonLabel.trim() !== '' && destination.trim() !== '';
    const bothEmpty = buttonLabel.trim() === '' && destination.trim() === '';

    if (bothFilled || bothEmpty) {
      clearErrors('calls_to_action');
    }
  }, [buttonLabel, destination, clearErrors]);

  return (
    <Card
      border="grays-ultralight"
      className={styles['sectionCard']}
      id="calls_to_action"
    >
      <Flex flexDirection="column" gap={16} p={40}>
        <Typography variant="header-bold">Call-to-action</Typography>
        <Flex gap={40}>
          <Typography>
            Add a call-to-action with an accompanying blurb. It will display
            under the image—if you chose to use one. Leave the field empty if
            you don't want it to display on your footer.
          </Typography>
          <CTAWireframe alignment={layout ?? 'align_left'} />
        </Flex>

        <Flex flexDirection="column" gap={16}>
          <Form.TextField
            name="calls_to_action.0.header"
            label="Header"
            maxLength={MAX_HEADER_LENGTH}
            helperText={
              <MaxCharacterHelper
                value={headerValue}
                maxLength={MAX_HEADER_LENGTH}
              />
            }
          />

          <Form.TextAreaField
            name="calls_to_action.0.body"
            label="Body"
            maxLength={MAX_BODY_LENGTH}
            helperText={
              <MaxCharacterHelper
                value={bodyValue}
                maxLength={MAX_BODY_LENGTH}
              />
            }
          />

          <Flex gap={24}>
            <Form.TextField
              name="calls_to_action.0.button_label"
              label="Button text"
              maxLength={MAX_BUTTON_TEXT_LENGTH}
              helperText={
                <MaxCharacterHelper
                  value={buttonLabel}
                  maxLength={MAX_BUTTON_TEXT_LENGTH}
                />
              }
              validate={validateButtonLabel}
            />

            <Form.TextField
              name="calls_to_action.0.destination"
              label="Button destination (URL)"
              type="url"
              ref={urlRef}
              validate={validateDestination}
            />
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};
